import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    BodyUpdateSurvey,
    SurveyDetailsItem,
    useGetSurveyDetails,
    useUpdateSurvey,
} from '@core/api/useSurveys';
import { Route } from '@core/routes/Route';
import useForm, { UseFormReturn } from '@core/hooks/useForm';
import { toast } from "react-toastify";
import { useCloseSurveyItems, usePublishSurveyItems } from '@core/api/useSurveyItems';
import { SurveyStatusIdEnum } from '@core/hooks/useCommonEnums';
import { AxiosResponse } from 'axios';

export type UseSurveyDetailsReturnType = {
    data: {
        surveyDetails: ReturnType<typeof useGetSurveyDetails>['data'];
        activeItems: SurveyDetailsItem[];
        isLoading: boolean;
    };
    form: UseFormReturn<BodyUpdateSurvey>;
    onDeleteItems: (itemIds?: number[]) => void;
    onSaveSurvey: (itemsToCloseOrPublish?: number[]) => void;
    getSurveyDetails: () => Promise<void | AxiosResponse>;
};

const useSurveyDetails = (): UseSurveyDetailsReturnType => {
    const navigate = useNavigate();
    const { id: surveyId } = useParams();

    const {
        state,
        actions
    } = useForm<BodyUpdateSurvey>({});

    const {
        doFetch: saveSurvey,
        isLoading: isSavingSurvey
    } = useUpdateSurvey();
    const {
        doFetch: closeSurveyItems,
        isLoading: isClosingSurveyItems
    } = useCloseSurveyItems();
    const {
        doFetch: publishSurveyItems,
        isLoading: isPublishingSurveyItems
    } = usePublishSurveyItems();

    const {
        data: surveyDetails,
        isLoading,
        doFetch: getSurveyDetails,
    } = useGetSurveyDetails(surveyId);

    useEffect(() => {
        if (surveyDetails) {
            actions.setFields({
                ...surveyDetails,
                itemsPerRespondent: {
                    regular: surveyDetails?.itemsPerRespondent?.regular ?? 0,
                    conceptLocker: surveyDetails?.itemsPerRespondent?.conceptLocker ?? 0,
                    express: surveyDetails?.itemsPerRespondent?.express ?? 0,
                    scoresPlus: surveyDetails?.itemsPerRespondent?.scoresPlus ?? 0,
                    scoresEnterprise: surveyDetails?.itemsPerRespondent?.scoresEnterprise ?? 0,
                },
                selectedItems: surveyDetails?.items?.flatMap((item) => item?.id ?? []),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyDetails]);

    const activeItems = useMemo(() => {
        return (surveyDetails?.items ?? [])?.filter((item) => state?.selectedItems?.includes(item?.id ?? 0)) ?? [];
    }, [surveyDetails?.items, state?.selectedItems]);

    const onDeleteItems = useCallback(
        (itemIds?: number[]) => {
            actions.setField(
                'selectedItems',
                (state?.selectedItems ?? [])?.filter((id) => !(itemIds)?.includes(id)) ?? [],
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.selectedItems],
    );

    const updateSurveyItems = useCallback(
        async (itemsToCloseOrPublish?: number[]) => {
            if (!itemsToCloseOrPublish?.length || surveyDetails?.status === SurveyStatusIdEnum.Draft) {
                return;
            }

            const isClosing = surveyDetails?.status === SurveyStatusIdEnum.Live;
            const updateSurveyItems = isClosing ? closeSurveyItems : publishSurveyItems;
            const action = isClosing ? 'closed' : 'published';

            try {
                const response = await updateSurveyItems({
                    data: { surveyId: surveyDetails?.id, selectedItems: itemsToCloseOrPublish },
                });
                
                if (response?.status === 200) {
                    toast.success(`${itemsToCloseOrPublish.length} survey items ${action} successfully`);
                } else {
                    toast.error(`${isClosing ? 'Closing' : 'Publishing'} survey items failed`);
                }
            } catch (error) {
                toast.error(`${isClosing ? 'Closing' : 'Publishing'} survey items failed`);
                console.error(error);
            }
        },
        [surveyDetails?.status, surveyDetails?.id, closeSurveyItems, publishSurveyItems],
    );

    const onSaveSurvey = useCallback((itemsToCloseOrPublish?: number[]) => {
        const perRespondentSum = Object.values(state?.itemsPerRespondent ?? {}).reduce(
            (acc, val) => acc + Number(val),
            0,
        );

        if (perRespondentSum === 0) {
            toast.warning('The total sum of the "per respondent" item sample sizes must be greater than 0');
            return;
        }

        toast.info('Updating survey details...');
        updateSurveyItems(itemsToCloseOrPublish).then(() => {
            saveSurvey({ data: state })
                .then((r) => {
                    if (r?.status === 200) {
                        toast.success('Survey saved successfully');

                        return navigate(Route.Surveys);
                    }

                    toast.error('Survey save failed');
                })
                .catch(() => {
                    toast.error('Survey save failed');
                });
        });
    }, [navigate, saveSurvey, state, updateSurveyItems]);

    const isLoadingOrSaving = useMemo(() => {
        // Sonar complains about the use of nullish coalescing here, but it's not nullish since all are booleans
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return isLoading || isSavingSurvey || isClosingSurveyItems || isPublishingSurveyItems;
    }, [isLoading, isSavingSurvey, isClosingSurveyItems, isPublishingSurveyItems]);

    return {
        data: {
            surveyDetails,
            activeItems,
            isLoading: isLoadingOrSaving,
        },
        form: {
            state,
            actions,
        },
        onDeleteItems,
        onSaveSurvey,
        getSurveyDetails,
    };
};

export default useSurveyDetails;
